import { useState, useEffect } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { 
  Ticket, 
  MessageSquare, 
  Clock, 
  CheckCircle, 
  User, 
  Mail,
  Building,
  Filter,
  Send
} from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { useToast } from "@/hooks/use-toast";
import { formatDistanceToNow } from "date-fns";
import type { SupportTicket, FAQItem } from "@/types/support";

const SupportManagement = () => {
  const [tickets, setTickets] = useState<SupportTicket[]>([]);
  const [faqs, setFaqs] = useState<FAQItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [replyMessage, setReplyMessage] = useState('');
  const [selectedTicket, setSelectedTicket] = useState<SupportTicket | null>(null);
  const [sending, setSending] = useState(false);
  const { user } = useAuth();
  const { toast } = useToast();

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [ticketsResponse, faqsResponse] = await Promise.all([
          supabase.from('support_tickets').select('*').order('created_at', { ascending: false }),
          supabase.from('faq_items').select('*').order('order_index', { ascending: true })
        ]);

        if (ticketsResponse.error) throw ticketsResponse.error;
        if (faqsResponse.error) throw faqsResponse.error;

        setTickets(ticketsResponse.data as SupportTicket[] || []);
        setFaqs(faqsResponse.data as FAQItem[] || []);
      } catch (error) {
        console.error('Error fetching data:', error);
        toast({
          title: "Error",
          description: "Failed to load support data",
          variant: "destructive"
        });
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [toast]);

  const updateTicketStatus = async (ticketId: string, status: string) => {
    try {
      const { error } = await supabase
        .from('support_tickets')
        .update({ 
          status,
          assigned_admin_id: user?.id,
          resolved_at: status === 'resolved' ? new Date().toISOString() : null
        })
        .eq('id', ticketId);

      if (error) throw error;

      setTickets(prev => prev.map(ticket =>
        ticket.id === ticketId ? { ...ticket, status: status as SupportTicket['status'], assigned_admin_id: user?.id } : ticket
      ));

      toast({
        title: "Ticket updated",
        description: `Ticket status changed to ${status}`,
      });
    } catch (error) {
      console.error('Error updating ticket:', error);
      toast({
        title: "Error",
        description: "Failed to update ticket",
        variant: "destructive"
      });
    }
  };

  const sendEmailReply = async (ticketId: string, message: string) => {
    setSending(true);
    try {
      const { error } = await supabase.functions.invoke('send-support-response', {
        body: {
          ticketId,
          message,
          adminUserId: user?.id
        }
      });

      if (error) throw error;

      toast({
        title: 'Success',
        description: 'Email reply sent successfully',
      });

      setReplyMessage('');
      setSelectedTicket(null);
    } catch (error) {
      console.error('Error sending email reply:', error);
      toast({
        title: 'Error',
        description: 'Failed to send email reply',
        variant: 'destructive',
      });
    } finally {
      setSending(false);
    }
  };

  const filteredTickets = tickets.filter(ticket => 
    statusFilter === 'all' || ticket.status === statusFilter
  );

  const getStatusCounts = () => {
    return {
      open: tickets.filter(t => t.status === 'open').length,
      in_progress: tickets.filter(t => t.status === 'in_progress').length,
      waiting_for_response: tickets.filter(t => t.status === 'waiting_for_response').length,
      resolved: tickets.filter(t => t.status === 'resolved').length,
    };
  };

  if (loading) {
    return <div className="space-y-4">Loading support data...</div>;
  }

  return (
    <div className="space-y-8">
      <Tabs defaultValue="tickets" className="space-y-6">
        <TabsList className="grid w-full max-w-md grid-cols-2">
          <TabsTrigger value="tickets">Support Tickets</TabsTrigger>
          <TabsTrigger value="faqs">FAQ Management</TabsTrigger>
        </TabsList>

        <TabsContent value="tickets" className="space-y-6">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle>Support Ticket Inbox</CardTitle>
                <CardDescription>Manage customer support tickets and respond directly</CardDescription>
              </div>
              <div className="flex items-center space-x-2">
                <Filter className="h-4 w-4" />
                <Select value={statusFilter} onValueChange={setStatusFilter}>
                  <SelectTrigger className="w-[180px]">
                    <SelectValue placeholder="Filter by status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Status</SelectItem>
                    <SelectItem value="open">Open</SelectItem>
                    <SelectItem value="in_progress">In Progress</SelectItem>
                    <SelectItem value="waiting_for_response">Waiting</SelectItem>
                    <SelectItem value="resolved">Resolved</SelectItem>
                    <SelectItem value="closed">Closed</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </CardHeader>
            <CardContent>
              {/* Ticket Summary */}
              <div className="grid grid-cols-4 gap-4 mb-6">
                <Card>
                  <CardContent className="pt-6">
                    <div className="text-2xl font-bold text-red-600">{getStatusCounts().open}</div>
                    <p className="text-sm text-muted-foreground">Open</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="pt-6">
                    <div className="text-2xl font-bold text-yellow-600">{getStatusCounts().in_progress}</div>
                    <p className="text-sm text-muted-foreground">In Progress</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="pt-6">
                    <div className="text-2xl font-bold text-blue-600">{getStatusCounts().waiting_for_response}</div>
                    <p className="text-sm text-muted-foreground">Waiting</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="pt-6">
                    <div className="text-2xl font-bold text-green-600">{getStatusCounts().resolved}</div>
                    <p className="text-sm text-muted-foreground">Resolved</p>
                  </CardContent>
                </Card>
              </div>

              {/* Tickets Table */}
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Ticket ID</TableHead>
                    <TableHead>Subject</TableHead>
                    <TableHead>User Email</TableHead>
                    <TableHead>Description</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Created</TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredTickets.map(ticket => (
                    <TableRow key={ticket.id}>
                      <TableCell className="font-mono text-sm">{ticket.ticket_number}</TableCell>
                      <TableCell className="font-medium max-w-xs truncate">
                        {ticket.subject}
                      </TableCell>
                      <TableCell>
                        <div>
                          <div className="font-medium">{ticket.user_email}</div>
                          <div className="text-sm text-muted-foreground">{ticket.user_name}</div>
                          {ticket.user_organization && (
                            <div className="text-xs text-muted-foreground">{ticket.user_organization}</div>
                          )}
                        </div>
                      </TableCell>
                      <TableCell className="max-w-xs">
                        <div className="text-sm text-muted-foreground truncate">
                          {ticket.description}
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge 
                          variant={
                            ticket.status === 'open' ? 'destructive' :
                            ticket.status === 'in_progress' ? 'default' :
                            ticket.status === 'waiting_for_response' ? 'secondary' :
                            ticket.status === 'resolved' ? 'outline' : 'secondary'
                          }
                        >
                          {ticket.status.replace('_', ' ')}
                        </Badge>
                      </TableCell>
                      <TableCell>{formatDistanceToNow(new Date(ticket.created_at))} ago</TableCell>
                      <TableCell>
                        <div className="flex space-x-2">
                          <Select 
                            value={ticket.status} 
                            onValueChange={(value) => updateTicketStatus(ticket.id, value)}
                          >
                            <SelectTrigger className="w-[130px]">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="open">Open</SelectItem>
                              <SelectItem value="in_progress">In Progress</SelectItem>
                              <SelectItem value="waiting_for_response">Waiting</SelectItem>
                              <SelectItem value="resolved">Resolved</SelectItem>
                              <SelectItem value="closed">Closed</SelectItem>
                            </SelectContent>
                          </Select>
                          <Dialog>
                            <DialogTrigger asChild>
                              <Button 
                                variant="outline" 
                                size="sm"
                                onClick={() => setSelectedTicket(ticket)}
                              >
                                <Mail className="w-4 h-4" />
                              </Button>
                            </DialogTrigger>
                            <DialogContent className="max-w-lg">
                              <DialogHeader>
                                <DialogTitle>Reply to Ticket: {ticket.ticket_number}</DialogTitle>
                              </DialogHeader>
                              <div className="space-y-4">
                                <div className="bg-muted p-4 rounded-lg">
                                  <div className="text-sm font-medium">To: {ticket.user_email}</div>
                                  <div className="text-sm text-muted-foreground mt-1">
                                    Subject: Re: {ticket.subject}
                                  </div>
                                  <div className="text-sm text-muted-foreground mt-2">
                                    Original message: "{ticket.description}"
                                  </div>
                                </div>
                                <div>
                                  <label className="text-sm font-medium">Your Response</label>
                                  <Textarea
                                    value={replyMessage}
                                    onChange={(e) => setReplyMessage(e.target.value)}
                                    placeholder="Type your response here..."
                                    rows={6}
                                    className="mt-1"
                                  />
                                </div>
                                <div className="flex justify-end space-x-2">
                                  <Button 
                                    variant="outline" 
                                    onClick={() => {
                                      setReplyMessage('');
                                      setSelectedTicket(null);
                                    }}
                                  >
                                    Cancel
                                  </Button>
                                  <Button 
                                    onClick={() => sendEmailReply(ticket.id, replyMessage)}
                                    disabled={!replyMessage.trim() || sending}
                                  >
                                    <Send className="w-4 h-4 mr-2" />
                                    {sending ? 'Sending...' : 'Send Reply'}
                                  </Button>
                                </div>
                              </div>
                            </DialogContent>
                          </Dialog>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="faqs" className="space-y-6">
          <div>
            <h3 className="text-2xl font-bold">FAQ Management</h3>
            <p className="text-muted-foreground">Manage frequently asked questions</p>
          </div>

          <div className="space-y-4">
            {faqs.map((faq) => (
              <Card key={faq.id}>
                <CardContent className="p-6">
                  <div className="flex justify-between items-start gap-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <Badge variant="secondary">{faq.category}</Badge>
                        {!faq.is_published && (
                          <Badge variant="outline" className="text-orange-600">
                            Draft
                          </Badge>
                        )}
                      </div>
                      <h4 className="font-semibold mb-2">{faq.question}</h4>
                      <p className="text-sm text-muted-foreground line-clamp-3">
                        {faq.answer}
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default SupportManagement;